/**
 * Samples the hummingbird silhouette (static/colibri.png, K2_nb) into particle targets.
 * Returns a Float32Array of vec4 per particle: x, y, z, part (0 = body/head/tail, 1 = wing).
 *
 * Parts: the PNG has 3 connected shapes (wings+body, head+beak, tail). The wings are split
 * from the body by BODY_POLYGON, drawn by hand in image pixels over the 1024 px source.
 */
const BODY_POLYGON = [[455, 500], [545, 478], [585, 515], [575, 560], [480, 640], [478, 548]]
export const HINGE_PX = [520, 505] // shoulder the wings rotate around
export const CENTER_PX = [510, 510]
export const PX_PER_UNIT = 200

const insidePolygon = (x, y, poly) =>
{
    let inside = false
    for(let i = 0, j = poly.length - 1; i < poly.length; j = i++)
    {
        const [xi, yi] = poly[i]
        const [xj, yj] = poly[j]
        if(((yi > y) !== (yj > y)) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
            inside = !inside
    }
    return inside
}

export const toWorld = ([x, y]) => [(x - CENTER_PX[0]) / PX_PER_UNIT, -(y - CENTER_PX[1]) / PX_PER_UNIT]

const loadImage = (url) => new Promise((resolve, reject) =>
{
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = url
})

export const sampleHummingbird = async (url, count, edgeShare = 0.35) =>
{
    const image = await loadImage(url)
    const size = image.width
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = size
    const context = canvas.getContext('2d', { willReadFrequently: true })
    context.drawImage(image, 0, 0)
    const pixels = context.getImageData(0, 0, size, size).data

    const isInk = (x, y) =>
    {
        if(x < 0 || y < 0 || x >= size || y >= size) return false
        const i = (y * size + x) * 4
        return (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3 < 128
    }

    // Label connected shapes so the wings can only come from the biggest one (wings + body)
    const labels = new Int32Array(size * size).fill(-1)
    const areas = []
    for(let y = 0; y < size; y++)
        for(let x = 0; x < size; x++)
        {
            if(!isInk(x, y) || labels[y * size + x] !== -1) continue
            const id = areas.length
            let area = 0
            const stack = [y * size + x]
            labels[y * size + x] = id
            while(stack.length)
            {
                const p = stack.pop()
                area++
                const px = p % size
                const py = (p - px) / size
                for(const [nx, ny] of [[px + 1, py], [px - 1, py], [px, py + 1], [px, py - 1]])
                {
                    if(!isInk(nx, ny) || labels[ny * size + nx] !== -1) continue
                    labels[ny * size + nx] = id
                    stack.push(ny * size + nx)
                }
            }
            areas.push(area)
        }
    const wingShape = areas.indexOf(Math.max(...areas))

    const fill = []
    const edge = []
    for(let y = 0; y < size; y++)
        for(let x = 0; x < size; x++)
        {
            const label = labels[y * size + x]
            if(label === -1 || areas[label] < 200) continue
            const wing = label === wingShape && !insidePolygon(x, y, BODY_POLYGON) ? 1 : 0
            const point = [x, y, wing]
            fill.push(point)
            if(!isInk(x + 1, y) || !isInk(x - 1, y) || !isInk(x, y + 1) || !isInk(x, y - 1))
                edge.push(point)
        }

    const data = new Float32Array(count * 4)
    const edgeCount = Math.floor(count * edgeShare)
    for(let i = 0; i < count; i++)
    {
        const source = i < edgeCount ? edge : fill
        const [x, y, wing] = source[Math.floor(Math.random() * source.length)]
        const [wx, wy] = toWorld([x + Math.random(), y + Math.random()])
        // Thin wings, a rounder body: depth gives the orbit camera something to read
        const depth = wing ? 0.02 : 0.1
        data[i * 4 + 0] = wx
        data[i * 4 + 1] = wy
        data[i * 4 + 2] = (Math.random() * 2 - 1) * depth
        data[i * 4 + 3] = wing
    }
    return data
}
